import { RawLocation } from "vue-router";
import { VanToast } from "vant/types/toast";
import { Toast } from "vant";
import qs from "query-string";
import { Native } from "@hb/mbridge";
import { throttle } from "lodash";
import { CategoryId, transformBusinessEum } from "./constants/commonConstants";
import { appQueryConfig } from "./utils/url";
import logger from "./utils/logger";

export interface UiInterface {
  init(params?: any): any;
  showToast(params?: any): any;
  hideTotast(params?: any): any;
  showLoading(params?: any): any;
  hideLoading(params?: any): any;
}

export type PageParams = {
  params?: any;
  notAni?: boolean;
  onComplete?: () => void;
  onAbort?: () => void;
  inheritQuery?: boolean; // 是否继承当前页面的查询参数，默认 true
};

export enum SKIP_ENUM {
  NONE = "",
  FORWARD = "FORWARD", // 前进
  BACK = "BACK", // 后退
  REDIRECT = "REDIRECT",
}

export type SkipData = {
  skipType: SKIP_ENUM; // 跳转方式
  notAni?: boolean; // 是否需要跳转动画
};

/**
 * 过滤继承的查询参数，减少 URL 长度和冗余
 * 1. 排除已经存在于 appQueryConfig 中的参数（避免重复）
 * 2. 排除空值参数
 */
const filterInheritedQuery = (
  currentQuery: Record<string, any>,
): Record<string, any> => {
  // 如果没有当前查询参数，直接返回空对象
  if (!currentQuery || Object.keys(currentQuery).length === 0) {
    return {};
  }

  // appQueryConfig 的 key 集合（用于快速查找）
  const appQueryKeys = new Set(Object.keys(appQueryConfig));

  // 使用 reduce 函数式过滤
  return Object.entries(currentQuery).reduce<Record<string, any>>((acc, [key, value]) => {
    // 过滤条件：
    // 1. 值不为空
    // 2. 不在 appQueryConfig 中（避免重复，因为 appQueryConfig 会再次合并）
    if (value !== '' && value !== null && value !== undefined && !appQueryKeys.has(key)) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const getResolvedLocation = (
  location: RawLocation,
  currentQuery?: Record<string, any>,
) => {
  // 过滤并获取需要继承的查询参数
  const inheritedQuery = currentQuery ? filterInheritedQuery(currentQuery) : {};

  if (typeof location === "string") {
    return qs.stringifyUrl({
      url: location,
      query: {
        ...inheritedQuery, // 继承当前页面的查询参数（已过滤）
        ...appQueryConfig, // 全局参数
      },
    });
  }

  const { query, ...rest } = location;
  return {
    ...rest,
    query: {
      ...inheritedQuery, // 继承当前页面的查询参数（已过滤）
      ...appQueryConfig, // 全局参数
      ...query, // 用户自定义参数（优先级最高）
    },
  };
};

/**
 * 页面管理器类
 * 用于处理页面跳转等
 */
export class UiMgr implements UiInterface {
  protected _isInit = false;

  protected _router: any = null;

  private _pageArr: RawLocation[] = [];

  // 是否正在showLoding中
  private _loading: VanToast | null = null;

  // skipStatus: SKIP_ENUM = SKIP_ENUM.NONE;

  skipData!: SkipData;

  private _tempParams: any;

  get tempParams() {
    const a = this._tempParams;
    this._tempParams = null;
    return a;
  }

  set tempParams(p: any) {
    this._tempParams = p;
    logger("set tempParams:", this._tempParams);
  }

  init({ router }: { router: any }) {
    if (this._isInit) {
      return;
    }

    if (router) {
      this._isInit = true;
      this._router = router;
    }
  }

  pushPage(
    location: RawLocation,
    {
      notAni, onComplete, onAbort, params, inheritQuery = true, // ⭐ 默认为 true
    }: PageParams = {},
  ) {
    if (!this._router) {
      return;
    }

    this.tempParams = params;
    this.skipData = {
      skipType: SKIP_ENUM.FORWARD,
      notAni,
    };

    // 如果需要继承当前页面的查询参数（默认继承）
    const currentQuery = inheritQuery ? this._router.currentRoute?.query : undefined;
    const resolvedLocation = getResolvedLocation(location, currentQuery);

    this._pageArr.push(resolvedLocation);
    this._router.push(resolvedLocation, onComplete, onAbort);
  }

  replacePage(
    location: RawLocation,
    {
      inheritQuery = true, // ⭐ 默认为 true
    }: PageParams = {},
  ) {
    // 如果需要继承当前页面的查询参数（默认继承）
    const currentQuery = inheritQuery ? this._router.currentRoute?.query : undefined;
    this._router.replace(getResolvedLocation(location, currentQuery));
  }

  private _throttlePopPage!: () => void;

  popPage({ notAni, params }: PageParams = {}) {
    this.tempParams = params;

    if (!this._throttlePopPage) {
      this._throttlePopPage = throttle(() => {
        if (!this._router) {
          return;
        }
        this.skipData = {
          skipType: SKIP_ENUM.BACK,
          notAni,
        };
        this._pageArr.pop();
        // this._router.go(-1)
        Native.goBack();
      }, 500);
    }

    this._throttlePopPage();
  }

  /**
   * n需要小与0，目前约定只能返回页面
   * @param n
   */
  gotoPage(n: number, { notAni }: PageParams = {}) {
    if (!this._router || !(n < 0)) {
      return;
    }

    if (n > 0) {
      this.skipData = {
        skipType: SKIP_ENUM.FORWARD,
        notAni,
      };
    } else if (n < 0) {
      this.skipData = {
        skipType: SKIP_ENUM.BACK,
        notAni,
      };
    }

    this._router.go(n);

    const fN = Math.floor(n);

    const pageLength = this._pageArr.length;
    if (pageLength + fN > 0) {
      this._pageArr.splice(this._pageArr.length + fN - 1, fN);
    }
  }

  /**
   * 根据页面名称返回到相应页面
   * @param pageName 页面名称
   */
  backPageByName(pageName: string, pageParams: PageParams = {}) {
    if (!pageName) {
      return;
    }

    let findNameIndex = -1;
    for (let i = 0; i < this._pageArr.length; ++i) {
      const pageCell: any = this._pageArr[i];
      if (pageCell && (pageName === pageCell || pageName === pageCell.name)) {
        findNameIndex = i;
        break;
      }
    }

    if (findNameIndex === -1) {
      this.popPage(pageParams);
    } else {
      this.gotoPage(findNameIndex - this._pageArr.length + 1, pageParams);
    }
  }

  gotoHomePage({ params }: PageParams = {}) {
    this.tempParams = params;
    this.replacePage("/bounty");
  }

  redirectPage(
    location: RawLocation,
    {
      notAni, onComplete, onAbort, inheritQuery = true, // ⭐ 默认为 true
    }: PageParams = {},
  ) {
    if (!this._router) {
      return;
    }
    this.skipData = {
      skipType: SKIP_ENUM.REDIRECT,
      notAni,
    };

    // 如果需要继承当前页面的查询参数（默认继承）
    const currentQuery = inheritQuery ? this._router.currentRoute?.query : undefined;
    const resolvedLocation = getResolvedLocation(location, currentQuery);

    this._router.replace(resolvedLocation, onComplete, onAbort);
  }

  switchPage() {
    if (!this._router) {
      logger("switchPage no router");
    }
  }

  showToast(...totalParams: Parameters<Toast>) {
    if (this._loading) {
      this.hideLoading();
    }

    Toast(...totalParams);
  }

  hideTotast() {
    Toast.clear();
  }

  showLoading(loadingMsg = "加载中...") {
    if (this._loading) {
      return null;
    }

    this._loading = Toast.loading({
      duration: 0, // 持续展示 toast
      forbidClick: true, // 禁用背景点击
      message: loadingMsg,
    });
    return this._loading;
  }

  hideLoading() {
    if (this._loading) {
      this._loading.clear();
      this._loading = null;
    }
  }

  private isSchemeUrl(url: string) {
    if (url && url.indexOf("hellobike://") >= 0) {
      return true;
    }
    return false;
  }

  /**
   * 打开hello app原生页面
   */
  showNativePage(schemeUrl: string) {
    if (!this.isSchemeUrl(schemeUrl)) {
      return;
    }
    window.location.href = schemeUrl;
  }

  /**
   * 去app首页各个业务线
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  gotoHbAppMainPage(categoryId: CategoryId) {
    const businessType = transformBusinessEum(categoryId);
    window.location.replace(
      `hellobike://hellobike.com/coupon?bikeType=${businessType}`,
    );
  }
}

/** 页面管理器 */
export const uiMgr = new UiMgr();
